import type { EditorConfig, LanguageInfo } from '@/types/monaco-editor'

export const SUPPORTED_LANGUAGES: LanguageInfo[] = [
  { id: 'typescript', name: 'TypeScript', extension: 'ts', aliases: ['ts'] },
  { id: 'javascript', name: 'JavaScript', extension: 'js', aliases: ['js', 'mjs'] },
  { id: 'python', name: 'Python', extension: 'py', aliases: ['py', 'pyw'] },
  { id: 'java', name: 'Java', extension: 'java', aliases: ['java'] },
  { id: 'csharp', name: 'C#', extension: 'cs', aliases: ['cs'] },
  { id: 'cpp', name: 'C++', extension: 'cpp', aliases: ['cpp', 'cc', 'cxx'] },
  { id: 'html', name: 'HTML', extension: 'html', aliases: ['html', 'htm'] },
  { id: 'css', name: 'CSS', extension: 'css', aliases: ['css'] },
  { id: 'json', name: 'JSON', extension: 'json', aliases: ['json'] },
  { id: 'markdown', name: 'Markdown', extension: 'md', aliases: ['md', 'markdown'] },
  { id: 'sql', name: 'SQL', extension: 'sql', aliases: ['sql'] },
  { id: 'yaml', name: 'YAML', extension: 'yaml', aliases: ['yaml', 'yml'] },
  { id: 'xml', name: 'XML', extension: 'xml', aliases: ['xml'] },
  { id: 'php', name: 'PHP', extension: 'php', aliases: ['php'] },
  { id: 'ruby', name: 'Ruby', extension: 'rb', aliases: ['rb', 'rbw'] },
  { id: 'go', name: 'Go', extension: 'go', aliases: ['go'] },
  { id: 'rust', name: 'Rust', extension: 'rs', aliases: ['rs'] },
  { id: 'swift', name: 'Swift', extension: 'swift', aliases: ['swift'] },
  { id: 'kotlin', name: 'Kotlin', extension: 'kt', aliases: ['kt', 'kts'] },
  { id: 'bash', name: 'Bash', extension: 'sh', aliases: ['sh', 'bash', 'zsh'] },
  { id: 'powershell', name: 'PowerShell', extension: 'ps1', aliases: ['ps1'] },
  { id: 'dockerfile', name: 'Dockerfile', extension: 'dockerfile', aliases: ['dockerfile'] },
  { id: 'graphql', name: 'GraphQL', extension: 'graphql', aliases: ['graphql', 'gql'] },
  { id: 'lua', name: 'Lua', extension: 'lua', aliases: ['lua'] },
  { id: 'perl', name: 'Perl', extension: 'pl', aliases: ['pl', 'pm'] },
  { id: 'r', name: 'R', extension: 'r', aliases: ['r'] },
  { id: 'scala', name: 'Scala', extension: 'scala', aliases: ['scala', 'sc'] },
  { id: 'vb', name: 'Visual Basic', extension: 'vb', aliases: ['vb'] },
  { id: 'objective-c', name: 'Objective-C', extension: 'm', aliases: ['m', 'mm'] },
  { id: 'dart', name: 'Dart', extension: 'dart', aliases: ['dart'] },
]

export const DEFAULT_EDITOR_CONFIG: EditorConfig = {
  defaultLanguage: 'typescript',
  defaultTheme: 'tailwind-dark',
  fontSize: 14,
  fontFamily: 'JetBrains Mono, Fira Code, Monaco, Consolas, monospace',
  tabSize: 2,
  wordWrap: 'on',
  minimap: true,
  lineNumbers: 'on',
  scrollBeyondLastLine: false,
  automaticLayout: true,
}

export const MONACO_THEMES = {
  light: 'tailwind-light',
  dark: 'tailwind-dark',
  vs: 'vs',
  'vs-dark': 'vs-dark',
  'hc-black': 'hc-black',
} as const

export const MONACO_OPTIONS = {
  basic: {
    fontSize: DEFAULT_EDITOR_CONFIG.fontSize,
    fontFamily: DEFAULT_EDITOR_CONFIG.fontFamily,
    tabSize: DEFAULT_EDITOR_CONFIG.tabSize,
    wordWrap: DEFAULT_EDITOR_CONFIG.wordWrap,
    minimap: { enabled: DEFAULT_EDITOR_CONFIG.minimap },
    lineNumbers: DEFAULT_EDITOR_CONFIG.lineNumbers,
    scrollBeyondLastLine: DEFAULT_EDITOR_CONFIG.scrollBeyondLastLine,
    automaticLayout: DEFAULT_EDITOR_CONFIG.automaticLayout,
    contextmenu: true,
    copyWithSyntaxHighlighting: true,
    emptySelectionClipboard: false,
    links: true,
    mouseWheelZoom: true,
    multiCursorModifier: 'ctrlCmd',
    renderControlCharacters: false,
    renderFinalNewline: false,
    renderWhitespace: 'selection',
    rulers: [],
    scrollbar: {
      horizontal: 'auto',
      vertical: 'auto',
      useShadows: true,
      verticalHasArrows: false,
      horizontalHasArrows: false,
      verticalScrollbarSize: 14,
      horizontalScrollbarSize: 14,
    },
    selectOnLineNumbers: true,
    showFoldingControls: 'mouseover',
    smoothScrolling: true,
    tabCompletion: 'on',
    wordBasedSuggestions: true,
    wordSeparators: '`~!@#$%^&*()-=+[{]}\\|;:\'",.<>/?',
  },
  minimal: {
    minimap: { enabled: false },
    lineNumbers: 'off',
    scrollBeyondLastLine: false,
    folding: false,
    glyphMargin: false,
    lineNumbersMinChars: 0,
    lineDecorationsWidth: 0,
    padding: { top: 8, bottom: 8 },
  },
} as const

export const WORKER_LANGUAGES = ['typescript', 'javascript', 'json', 'html', 'css', 'yaml', 'xml', 'markdown', 'sql', 'graphql'] as const
